import { useEffect, useId, useRef } from "react";
import { Marker } from "react-map-gl/mapbox";

import { useTranslation } from "@/lib/i18n";

import { pointerHasNoHover } from "./marker-tap";
import { riskPictureCellLabel } from "./cell-label";
import { scheduleRiskPictureClusterPreview } from "./risk-picture-cluster-preview";
import {
  RiskPictureMapMarker,
  RiskPictureMapMarkerCard,
  RiskPictureMapMarkerIcon,
  RiskPictureMapMarkerStackIcon,
} from "./risk-picture-map-marker";
import { riskPictureItemPosition, riskPictureMarkerZIndex } from "./risk-picture-layers";
import type { RiskPictureItem } from "./risk-picture-model";
import { riskPicturePhenomenonName } from "./risk-picture-phenomena";
import { useFadingMarkerPreview } from "./risk-picture-marker-preview-fade";
import styles from "./risk-picture.module.css";

interface RiskPictureMarkerClusterProps {
  /** Highest warning code first. */
  items: RiskPictureItem[];
  onSelect: (itemId: string) => void;
  previewItemId?: string;
  focusedItemId?: string;
  selectedItemId?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onHoverItem?: (itemId: string | undefined) => void;
}

/** Hover opens a stationary chooser; leaving the stack/chooser region closes it. */
export const RiskPictureMarkerCluster = ({
  items,
  onSelect,
  previewItemId,
  focusedItemId,
  selectedItemId,
  open,
  onOpenChange,
  onHoverItem,
}: RiskPictureMarkerClusterProps) => {
  const { t } = useTranslation();
  const listId = useId();
  const containerRef = useRef<HTMLDivElement>(null);
  const stackRef = useRef<HTMLButtonElement>(null);
  const cancelPreview = useRef<(() => void) | undefined>(undefined);
  const onOpenChangeRef = useRef(onOpenChange);
  onOpenChangeRef.current = onOpenChange;
  const top = items[0];
  const selectionInCluster = items.some((item) => item.id === selectedItemId);
  const previewItem = items.find((item) => item.id === previewItemId);
  const fadingPreviews = useFadingMarkerPreview(previewItem);

  const cancelPendingPreview = () => {
    cancelPreview.current?.();
    cancelPreview.current = undefined;
  };
  const clearPreview = () => {
    cancelPendingPreview();
    onHoverItem?.(undefined);
  };
  const preview = (itemId: string) => {
    // Retain the active warning while the next row's hover intent is pending.
    cancelPendingPreview();
    cancelPreview.current = scheduleRiskPictureClusterPreview(() => onHoverItem?.(itemId));
  };

  useEffect(() => {
    if (!open || selectionInCluster || previewItem) return;
    const closeOutside = (event: PointerEvent) => {
      if (event.target instanceof Node && !containerRef.current?.contains(event.target)) {
        onOpenChangeRef.current(false);
      }
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.stopPropagation();
      onOpenChangeRef.current(false);
      stackRef.current?.focus();
    };
    document.addEventListener("pointerdown", closeOutside);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      cancelPreview.current?.();
      onHoverItem?.(undefined);
      document.removeEventListener("pointerdown", closeOutside);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [open, selectionInCluster, previewItem, onHoverItem]);

  // Sidebar/timeline hover identifies one warning, not the whole stack.
  // It takes precedence even when another member has a polygon-only detail view.
  if (previewItem || fadingPreviews.length > 0) {
    return (
      <>
        {fadingPreviews.map((entry) => {
          const [longitude, latitude] = riskPictureItemPosition(entry.item);
          // A departing card must never intercept a click or change map hover focus.
          const leaving = entry.leaving || entry.item.id !== previewItem?.id;
          return (
            <Marker
              key={entry.item.id}
              longitude={longitude}
              latitude={latitude}
              anchor="center"
              style={{
                zIndex: leaving ? 4 : 5,
                pointerEvents: leaving ? "none" : "auto",
              }}
              onClick={(event) => event.originalEvent.stopPropagation()}
            >
              <div
                className={styles.markerPreviewFade}
                style={{ opacity: entry.opacity }}
                ref={(node) => {
                  // React 18's HTML typings predate inert; set it on the DOM node.
                  if (leaving) node?.setAttribute("inert", "");
                  else node?.removeAttribute("inert");
                }}
                aria-hidden={leaving || undefined}
              >
                <RiskPictureMapMarkerCard
                  item={entry.item}
                  onSelect={() => onSelect(entry.item.id)}
                  previewed
                  onHoverChange={
                    leaving
                      ? undefined
                      : (hovered) => onHoverItem?.(hovered ? entry.item.id : undefined)
                  }
                />
              </div>
            </Marker>
          );
        })}
      </>
    );
  }

  // Detail view is polygon-only: remove the stack and its hover hit target entirely.
  if (!top || selectionInCluster) return null;

  if (items.length === 1) {
    return (
      <RiskPictureMapMarker
        item={top}
        onSelect={() => onSelect(top.id)}
        previewed={top.id === previewItemId || top.id === selectedItemId}
        dimmed={Boolean(focusedItemId && top.id !== focusedItemId)}
        onHoverItem={onHoverItem}
      />
    );
  }

  const focusInCluster = items.some((item) => item.id === focusedItemId);
  const displayedItem = items.find((item) => item.id === focusedItemId) ?? top;
  const [longitude, latitude] = riskPictureItemPosition(top);
  const label = t("riskPicture.map.clusterAria", { count: items.length });

  return (
    <Marker
      longitude={longitude}
      latitude={latitude}
      anchor="center"
      style={{ zIndex: riskPictureMarkerZIndex(top, open || focusInCluster) }}
      onClick={(event) => event.originalEvent.stopPropagation()}
    >
      <div
        ref={containerRef}
        className={styles.markerCluster}
        data-map-chrome=""
        onMouseLeave={() => {
          clearPreview();
          onOpenChange(false);
        }}
      >
        <button
          ref={stackRef}
          type="button"
          className={styles.markerClusterStack}
          aria-label={label}
          aria-expanded={open}
          aria-controls={open ? listId : undefined}
          onMouseEnter={() => {
            if (!pointerHasNoHover()) onOpenChange(true);
          }}
        >
          <RiskPictureMapMarkerStackIcon item={displayedItem} count={items.length} />
        </button>
        {open && (
          <ul id={listId} className={styles.markerClusterList} aria-label={label}>
            {items.map((item) => (
              <li key={item.id}>
                <button
                  type="button"
                  className={styles.markerClusterRow}
                  aria-pressed={selectedItemId === item.id}
                  data-previewed={focusedItemId === item.id || undefined}
                  onMouseEnter={() => {
                    if (!pointerHasNoHover()) preview(item.id);
                  }}
                  onMouseLeave={cancelPendingPreview}
                  onFocus={() => {
                    if (!pointerHasNoHover()) preview(item.id);
                  }}
                  onBlur={(event) => {
                    if (
                      event.relatedTarget instanceof Node &&
                      containerRef.current?.contains(event.relatedTarget)
                    ) {
                      cancelPendingPreview();
                    } else {
                      clearPreview();
                    }
                  }}
                  onClick={(event) => {
                    event.stopPropagation();
                    clearPreview();
                    onOpenChange(false);
                    onSelect(item.id);
                  }}
                >
                  <RiskPictureMapMarkerIcon item={item} />
                  <span className={styles.markerClusterRowText}>
                    <span className={styles.mapMarkerTitle}>
                      {riskPictureCellLabel(item)}
                    </span>
                    <span>{riskPicturePhenomenonName(item.phenomenon, t)}</span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Marker>
  );
};
